import {User} from "../../entity/model/user";
import {UserPermission} from "../../entity/enums/user-permission";
import UserStore from "../store/user-store";
import {ManageMode} from "../../entity/enums/manage-mode";
import {UserDto} from "../../entity/dto/user-dto";
import {RequestBody} from "../../entity/core/request-body";
import {toast} from "react-toastify";
import {UserRole} from "../../entity/enums/user-role";
import {SearchInputDto} from "../../entity/dto/search-input-dto";

export default class UserService {
    userStore: UserStore;

    constructor(userStore: UserStore) {
        this.userStore = userStore;
    }

    getCurrentUser = async (): Promise<User | undefined> => {
        return this.userStore.currentUser ?? await this.userStore.getCurrentUser();
    }

    getUserById = async(userId: string): Promise<User | undefined> => {
        return await this.userStore.getUserById(userId);
    }

    getAllUsers = async (organizationId: string, reload: boolean): Promise<User[]> => {
        return this.userStore.cache && this.userStore.users.filter((user: User) => user.organizationID === organizationId).length > 0 && !reload
            ? this.userStore.users.filter((user: User) => user.isActive)
            : (await this.userStore.getAllUsers(organizationId)).filter((user: User) => user.isActive);
    }

    createUser = async (organizationID: string, userPayload: UserDto): Promise<void> => {
        const requestBody: RequestBody = { body: userPayload };
        const response = await this.userStore.createUser(organizationID, requestBody);

        if (!response)
            toast.success('User created successfully !!');
        else
            toast.error(response);
    }

    updateUser = async (userId: string, userPayload: UserDto): Promise<void> => {
        const requestBody: RequestBody = {body: userPayload};
        const response = await this.userStore.updateUser(userPayload.organizationID, userId, requestBody);

        if (!response)
            toast.success('User updated successfully !!');
        else
            toast.error(response);
    }

    deleteUser = async (organizationId: string, userId: string): Promise<void> => {
        const response = await this.userStore.deleteUser(organizationId, userId);

        if (!response)
            toast.success('User deleted successfully !!');
        else
            toast.error(response);
    }

    logout = async (): Promise<void> => {
        const response = await this.userStore.logout();
        if (response)
            toast.error(response);
    }

    isLoggedIn = (): boolean => {
        return this.userStore.isLoggedIn;
    }

    setLoggedIn = (status: boolean): void => {
        this.userStore.isLoggedIn = status;
    }

    isPermitted = (permission: UserPermission): boolean => {
        const currentUser = this.userStore.currentUser;
        return currentUser ? currentUser.permissions.includes(permission.toString()) : false;
    }

    isAdminUser = (): boolean => {
        const currentUser = this.userStore.currentUser;
        return currentUser ? currentUser.roles.filter((role: string): boolean => role === UserRole.ADMIN || role === UserRole.ORG_ADMIN).length > 0 : false;
    }

    isSuperAdminUser = (): boolean => {
        const currentUser = this.userStore.currentUser;
        return currentUser ? currentUser.roles.filter((role: string): boolean => role === UserRole.ADMIN).length > 0 : false;
    }

    getDisplayName = (isCurrentUser: boolean, userId: string): string => {
        const currentUser = this.userStore.currentUser;
        const user = this.userStore.users.find((user: User) => user.id === userId);

        if (isCurrentUser)
            return currentUser ? currentUser.firstName + ' ' + currentUser.lastName : '';

        if (user)
            return user.firstName + ' ' + user.lastName;

        return '';
    }

    getCache = (): boolean => {
        return this.userStore.cache;
    }

    filterUsers = (input: string): User[] => {
        return this.userStore.users.filter((user: User) => {
            const matchesFirstName = user.firstName.trim().toLowerCase().startsWith(input);
            const matchesLastName = user.lastName.trim().toLowerCase().startsWith(input);
            const matchesEmail = user.email.trim().toLowerCase().startsWith(input);
            return matchesEmail || matchesFirstName || matchesLastName;
        });
    }

    getUserDto = (mode: ManageMode, organizationId: string, user?: User): UserDto => {
        return {
            organizationID: organizationId,
            firstName: mode === ManageMode.CREATE ? '' : user?.firstName ?? '',
            lastName: mode === ManageMode.CREATE ? '' : user?.lastName ?? '',
            email: mode === ManageMode.CREATE ? '' : user?.email ?? '',
            roles: mode === ManageMode.CREATE ? '' : user?.roles[0] ?? ''
        }
    }

    getSearchDto = (): SearchInputDto => {
        return {
            searchInput: ''
        };
    }
}
